import axios from 'axios';

// Configuração base da API
const api = axios.create({
  baseURL: "https://portalvendas-backend.santacasacopacabana.com.br/api",
  //baseURL: process.env.REACT_APP_API_DEV_URL || 'http://localhost:3001/api',
  //baseURL: 'https://backend-portal-homologacao.santacasacopacabana.com.br/api',
  timeout: process.env.REACT_APP_API_TIMEOUT || 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor de REQUISIÇÃO (para adicionar o token)
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('santa-casa-accessToken');
    const selectedContrato = localStorage.getItem('santa-casa-selected-contrato');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (selectedContrato) {
      const parsedContrato = JSON.parse(selectedContrato);
      config.headers['x-contract-number'] = parsedContrato.numero;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Interceptor de RESPOSTA (para lidar com o refresh token) ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;

    // --- AJUSTE CRÍTICO AQUI ---
    // Verifica se a URL da requisição original termina com as rotas de autenticação.
    const isLoginOrRefreshAttempt = originalRequest.url.endsWith('/auth/login') || originalRequest.url.endsWith('/auth/refresh-token');

    // Agora, a lógica de refresh só é acionada se o erro for 401,
    // E NÃO for uma tentativa de login ou de refresh.
    if (error.response?.status === 401 && !originalRequest._retry && !isLoginOrRefreshAttempt) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('santa-casa-refreshToken');
      if (!refreshToken) {
        // Se não houver refresh token, desloga
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await api.post('/auth/refresh-token', { token: refreshToken });
        const { accessToken: newAccessToken } = response.data.data;

        localStorage.setItem('santa-casa-accessToken', newAccessToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);

        return api(originalRequest);

      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('santa-casa-accessToken');
        localStorage.removeItem('santa-casa-refreshToken');
        delete api.defaults.headers.common['Authorization'];
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Se a condição acima não for atendida (ex: erro 401 na tela de login),
    // o erro é simplesmente retornado para ser tratado pela função que o chamou.
    return Promise.reject(error);
  }
);

// O restante do arquivo permanece o mesmo
// Funções de autenticação
export const authAPI = {
  login: (documento, senha) => api.post('/auth/login', { documento, senha }),
  validateToken: () => api.post('/auth/validate-token'),
  refreshToken: () => api.post('/auth/refresh-token')
};

export const notificationsAPI = {
  syncDevice: (payload) => api.post('/notifications/devices/sync', payload),
  disableDevice: (payload) => api.post('/notifications/devices/disable', payload),
};

export const avisosAPI = {
  pendentes: () => api.get('/avisos/pendentes'),
  registrarLeitura: (id) => api.post(`/avisos/${encodeURIComponent(id)}/leitura`),
};

// Funções do cliente
export const clienteAPI = {
  getDashboard: () => api.get('/cliente/dashboard'),
  getProfile: () => api.get('/cliente/perfil'),
};

// Funções de dependentes
export const dependenteAPI = {
  getAll: () => api.get('/dependente'),
  create: (dependente) => api.post('/dependente', dependente),
  update: (id, data) => api.put(`/dependente/${id}`, data),
  remove: (id) => api.delete(`/dependente/${id}`),
  getPrices: () => api.get('/dependente/precos'),
  getPriceByAge: (idade, dependentType = 'humano') => api.get(`/dependente/precos/${dependentType}/${idade}`)
};

// Funções financeiras
export const financeiroAPI = {
  getPayments: (params) => api.get('/financeiro/pagamentos', { params }),
  getPix: (idBoleto) => api.post(`/financeiro/pix?idBoleto=${idBoleto}`),
  checkPixStatus: (idBoleto, txid) => api.post(`/financeiro/pix/status/${idBoleto}?txid=${txid}`),
  getSegundaVia: (idBoleto) => api.get(`/financeiro/segunda-via/${idBoleto}`),
  getCardPaymentOptions: (idBoleto) => api.post('/financeiro/cartao/opcoes', { idBoleto }),
  getAsaasPaymentUrl: (idBoleto, options = {}) => api.post('/financeiro/asaas', { idBoleto, ...options }),
  validateAsaasLink: (idBoleto) => api.post('/financeiro/asaas/validar', { idBoleto }),
};

// Funções de planos
export const planosAPI = {
  getValores: () => api.get('/planos/valores'),
};

// Funções de Solicitações
export const requisicaoAPI = {
  // Legado (apps móveis)
  criar: (payload) => api.post('/requisicao', payload),
  // Compatibilidade (portal antigo)
  criarAutomatica: (payload) => api.post('/requisicao/automatica', payload),
  // Portal web — alteração cadastral com OTP
  solicitarAutomatica: (payload) => api.post('/requisicao/automatica/solicitar', payload),
  confirmarAutomatica: (payload) => api.post('/requisicao/automatica/confirmar', payload),
};

// Funções de protocolos
export const protocolosAPI = {
  criar: (payload) => api.post('/protocolos', payload),
  getAndamentos: (codigo, id) => api.get(
    `/protocolos/${encodeURIComponent(codigo)}/andamentos`,
    { params: { id } }
  ),
  adicionarAndamento: (codigo, payload) => api.post(
    `/protocolos/${encodeURIComponent(codigo)}/andamentos`,
    payload
  ),
};

// Funções de páginas (conteúdo dinâmico)
export const paginaAPI = {
  getCobertura: () => api.get('/pagina/cobertura'),
  getDashboard: () => api.get('/pagina/dashboard'),
};

// Funções de cartões
export const cartoesAPI = {
  gerar: (membros, contrato) => api.post('/cartoes/gerar', { membros, contrato }, { 
    responseType: 'blob' // Importante para receber o PDF
  }),
};

// Funções de manutenção
export const maintenanceAPI = {
  getConfig: () => api.get('/maintenance'),
};

// Helpers para formatação de dados
export const formatters = {
  formatDocumento: (documento) => {
    if (!documento) return '';
    const digits = String(documento).replace(/\D/g, '');
    if (digits.length > 11) {
      return digits.substring(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
    }
    return digits.substring(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
  },

  formatDocumentoOculto: (documento) => {
    if (!documento) return '';
    
    // Remove apenas espaços e caracteres especiais, mantendo dígitos e asteriscos
    const cleaned = String(documento).replace(/[^\d*]/g, '');
    
    // Conta o total de caracteres (dígitos + asteriscos)
    const totalLength = cleaned.length;
    
    // Determina se é CPF (11 caracteres) ou CNPJ (14 caracteres)
    if (totalLength === 11) {
      // Formatação CPF: XXX.XXX.XXX-XX
      // Exemplo: "914******04" -> "914.***.***-04"
      return cleaned
        .substring(0, 11)
        .replace(/(.{3})(.{3})(.{3})(.{2})/, '$1.$2.$3-$4');
    } else if (totalLength === 14) {
      // Formatação CNPJ: XX.XXX.XXX/XXXX-XX
      // Exemplo: "914******04" (14 chars) -> "91.4**.***/****-04"
      return cleaned
        .substring(0, 14)
        .replace(/(.{2})(.{3})(.{3})(.{4})(.{2})/, '$1.$2.$3/$4-$5');
    }
    
    // Se não tiver 11 ou 14 caracteres, retorna sem formatação
    return cleaned;
  },

  formatPhone: (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{4,5})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  },

  formatCurrency: (value) => {
    if (!value || isNaN(parseFloat(value))) return 'R$ 0,00';
    try {
      const numValue = parseFloat(value);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(numValue);
    } catch (error) {
      console.warn('Erro ao formatar valor:', value, error);
      return 'R$ 0,00';
    }
  },

  // Função auxiliar para converter string de data em Date local (sem timezone UTC)
  parseLocalDate: (date) => {
    if (!date) return null;
    
    // Se já for um objeto Date
    if (date instanceof Date) {
      return date;
    }
    
    // Se for string no formato ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
    if (typeof date === 'string' && date.includes('-')) {
      const datePart = date.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Se for string no formato DD/MM/YYYY
    if (typeof date === 'string' && date.includes('/')) {
      const [day, month, year] = date.split('/').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // Fallback: tentar criar Date normalmente
    return new Date(date);
  },

  getPaymentStatus: (vencimento, pagamento, statusOriginal) => {
    // Se já foi pago, mantém o status "Pago"
    if (statusOriginal === 'Pago' || pagamento) {
      return 'Pago';
    }

    // Converter datas para objetos Date usando parseLocalDate
    const vencimentoDate = formatters.parseLocalDate(vencimento);
    const hoje = new Date();
    
    // Resetar horas para comparar apenas as datas
    hoje.setHours(0, 0, 0, 0);
    if (vencimentoDate) {
      vencimentoDate.setHours(0, 0, 0, 0);
    }

    // Comparar as datas
    if (vencimentoDate && vencimentoDate.getTime() === hoje.getTime()) {
      return 'VENCE HOJE';
    } else if (vencimentoDate && vencimentoDate > hoje) {
      return 'A VENCER';
    } else {
      return 'EM ATRASO';
    }
  },

  // Função para verificar se um pagamento está em atraso
  isPaymentOverdue: (vencimento, pagamento, statusOriginal) => {
    // Se já foi pago, não está em atraso
    if (statusOriginal === 'Pago' || pagamento) return false;
    
    const vencimentoDate = formatters.parseLocalDate(vencimento);
    const hoje = new Date();
    
    // Resetar horas para comparar apenas as datas
    hoje.setHours(0, 0, 0, 0);
    if (vencimentoDate) {
      vencimentoDate.setHours(0, 0, 0, 0);
    }
    
    // Se a data de vencimento é anterior a hoje, está em atraso
    return vencimentoDate && vencimentoDate < hoje;
  },

  formatDate: (date) => {
    if (!date) return '';
    
    try {
      // Se já estiver em DD/MM/AAAA, retornar como está
      const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyy.test(date)) {
        return date;
      }
      
      // Tentar criar um objeto Date
      let parsedDate;
      
      // Se a data vier em formato ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
      if (typeof date === 'string' && date.includes('-')) {
        // Extrair apenas a parte da data (YYYY-MM-DD)
        const datePart = date.split('T')[0];
        const [year, month, day] = datePart.split('-').map(Number);
        
        // Criar data no fuso horário local (evita problemas de timezone)
        parsedDate = new Date(year, month - 1, day);
      } else {
        parsedDate = new Date(date);
      }
      
      // Verificar se a data é válida
      if (isNaN(parsedDate.getTime())) {
        return '';
      }
      
      return new Intl.DateTimeFormat('pt-BR').format(parsedDate);
    } catch (error) {
      console.warn('Erro ao formatar data:', date, error);
      return '';
    }
  },

  formatDateTime: (date) => {
    if (!date) return '';
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return '';
      }
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(parsedDate);
    } catch (error) {
      console.warn('Erro ao formatar data/hora:', date, error);
      return '';
    }
  },

  isPetPlan: (idPlano) => {
    const petPlans = ['567', '568', '569', '570', '571', '769', '782', '738'];
    return petPlans.includes(String(idPlano));
  },

  getPlanConfig: (idPlano, tipoPlano = false) => {
    if (tipoPlano === false){
      const isPet = formatters.isPetPlan(idPlano);
      if (isPet) {
        return {
          icon: '/pet.svg',
          borderColor: '#10b981',
          backgroundColor: '#f0fdf4',
          shadowColor: 'rgba(16, 185, 129, 0.15)',
          type: 'pet'
        };
      }
      return {
        icon: '/pessoas.svg',
        borderColor: '#3b82f6',
        backgroundColor: '#f0f9ff',
        shadowColor: 'rgba(59, 130, 246, 0.15)',
        type: 'pessoas'
      };
    } else {
      if (tipoPlano === 'PET') {
        return {
          icon: '/pet.svg',
          borderColor: '#10b981',
          backgroundColor: '#f0fdf4',
          shadowColor: 'rgba(16, 185, 129, 0.15)',
          type: 'pet'
        };
      }
      return {
        icon: '/pessoas.svg',
        borderColor: '#3b82f6',
        backgroundColor: '#f0f9ff',
        shadowColor: 'rgba(59, 130, 246, 0.15)',
        type: 'pessoas'
      };
    }
  },
};

export default api;
